import { loggerConfig } from '@/config'
// Local-first logger. Sentry hooks are disabled for local usage unless explicitly enabled.
import type { ColorValidationResult } from '@/types/notifications'
import * as Sentry from '@sentry/react'

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

// Error severity classification for intelligent filtering
export enum ErrorSeverity {
  CRITICAL = 'critical',    // Actual bugs - always send to Sentry
  IMPORTANT = 'important',  // User-facing issues - send to Sentry
  EXPECTED = 'expected',    // Expected failures - breadcrumb only
  NOISE = 'noise'           // Debug info - console only
}

// Error patterns for automatic severity classification
const ERROR_PATTERNS = {
  [ErrorSeverity.EXPECTED]: [
    /401/,                      // Auth expired
    /403/,                      // Forbidden
    /unauthorized/i,            // Auth issues
    /timeout/i,                 // Network timeouts
    /connection.*failed/i,      // Network failures
    /signature.*expired/i,      // JWT expired
    /failed to connect.*401/i,  // SSE auth expired
    /network.*error/i,          // Network issues
    /fetch.*failed/i,           // Fetch failures
    /circuit breaker/i,         // Circuit breaker operational events
    /threshold reached/i,       // Circuit breaker threshold events
    /opening circuit/i,         // Circuit breaker state changes
    /circuit.*opened/i,         // Circuit breaker opened
    /circuit.*closed/i,         // Circuit breaker closed
    /half.?open/i,              // Circuit breaker half-open state
  ],
  [ErrorSeverity.NOISE]: [
    /hydration/i,               // React hydration warnings
    /chunk.*failed/i,           // Code splitting failures (user refresh fixes)
    /loading.*chunk.*failed/i,  // Webpack chunk loading
    /script.*error/i,           // Script loading errors (often extensions)
    /extension:\/\//,           // Browser extension errors
  ]
}

interface LogConfig {
  enabled: boolean
  level: LogLevel
  includeTimestamp: boolean
  includeRequestId: boolean
}

// Type for log data that can be safely logged
type LogData = string | number | boolean | null | undefined | 
  Record<string, unknown> | Array<unknown> | Date

// Type for request body in RequestInit
// type RequestBody = string | FormData | URLSearchParams | Blob | ArrayBuffer | 
//   DataView | ReadableStream<Uint8Array> | null

// Interface for parsed error details
interface ErrorDetails {
  name: string
  message: string
  stack?: string
}

// Interface for log entry
// interface LogEntry {
//   level: LogLevel
//   message: string
//   data?: LogData
//   requestId?: string
//   timestamp?: string
// }

// Type guard to check if value is an Error
function isError(value: unknown): value is Error {
  return value instanceof Error || (
    typeof value === 'object' && 
    value !== null && 
    'message' in value && 
    typeof (value as Record<string, unknown>).message === 'string'
  )
}

// Type guard to check if value is a Record
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// Helper function to safely format errors
function formatError(error: unknown): ErrorDetails | string {
  if (isError(error)) {
    const details: ErrorDetails = {
      name: error.name,
      message: error.message,
    }
    if (typeof error.stack === 'string') {
      details.stack = error.stack
    }
    return details
  }
  
  if (typeof error === 'string') {
    return error
  }
  
  if (isRecord(error) && 'message' in error) {
    const details: ErrorDetails = {
      name: 'name' in error && typeof (error as Record<string, unknown>).name === 'string' ? String((error as Record<string, unknown>).name) : 'Error',
      message: String((error as Record<string, unknown>).message),
    }
    if ('stack' in error && typeof (error as Record<string, unknown>).stack === 'string') {
      details.stack = String((error as Record<string, unknown>).stack)
    }
    return details
  }
  
  // For any other type, convert to string with better error handling
  try {
    // Try to stringify with proper formatting
    const stringified = JSON.stringify(error, null, 2)
    // If it's just an empty object, provide more context
    if (stringified === '{}') {
      return `Empty object: ${Object.prototype.toString.call(error)}`
    }
    return stringified
  } catch {
    // If JSON.stringify fails, try to extract useful information
    if (error && typeof error === 'object') {
      const errorObj = error as Record<string, unknown>
      const keys = Object.keys(errorObj)
      if (keys.length === 0) {
        return `Empty object: ${Object.prototype.toString.call(error)}`
      }
      // Try to extract key information
      const info = keys.map(key => {
        try {
          return `${key}: ${String(errorObj[key])}`
        } catch {
          return `${key}: [unserializable]`
        }
      }).join(', ')
      return `Object: {${info}}`
    }
    return String(error)
  }
}

class Logger {
  private readonly isProduction = import.meta.env.PROD;

  private config: LogConfig = {
    enabled: loggerConfig.enabled || localStorage.getItem('debug') === 'true',
    level:
      loggerConfig.level === 'debug'
        ? LogLevel.DEBUG
        : loggerConfig.level === 'info'
        ? LogLevel.INFO
        : loggerConfig.level === 'warn'
        ? LogLevel.WARN
        : LogLevel.ERROR,
    includeTimestamp: true,
    includeRequestId: true,
  }

  // Control whether debug/info logs are sent to Sentry
  private sentryVerbose = false // Default disabled for local development

  private requestCounter = 0
  private activeRequests = new Map<string, number>()

  // Error rate limiting to prevent duplicate errors from spamming Sentry
  private errorFingerprints = new Map<string, number>() // fingerprint -> last sent timestamp
  private readonly ERROR_RATE_LIMIT_WINDOW = 60000 // 1 minute

  // Browser console color codes with validation
  private colors = {
    [LogLevel.DEBUG]: this.validateColor('color: #888'),
    [LogLevel.INFO]: this.validateColor('color: #2196F3'),
    [LogLevel.WARN]: this.validateColor('color: #FF9800'),
    [LogLevel.ERROR]: this.validateColor('color: #F44336')
  }

  // Validate hex color format to prevent runtime errors
  private validateColor(colorStyle: string): string {
    try {
      // Extract hex color from style string
      const colorMatch = colorStyle.match(/#[0-9A-Fa-f]{6}/)
      if (!colorMatch) {
        // Try to validate and fix common color format issues
        const validationResult = this.validateAndFixColor(colorStyle)
        if (validationResult.isValid) {
          return validationResult.normalizedColor
        }
        
        if (!this.isProduction) {
          console.warn('Invalid color format, falling back to default:', {
            original: colorStyle,
            error: validationResult.error,
          })
        }
        return 'color: #888' // Default gray
      }
      return colorStyle
    } catch (error) {
      if (!this.isProduction) {
        console.warn('Color validation failed, using default:', error)
      }
      return 'color: #888' // Default gray fallback
    }
  }

  // Enhanced color validation and normalization
  private validateAndFixColor(colorStyle: string): ColorValidationResult {
    try {
      // Remove 'color:' prefix and trim
      const colorValue = colorStyle.replace(/^color:\s*/, '').trim()
      
      // Check if it's a valid hex color
      const hexMatch = colorValue.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/)
      if (hexMatch) {
        const hex = hexMatch[1] ?? ''
        // Expand 3-digit hex to 6-digit
        if (hex.length === 3) {
          const expanded = hex.split('').map(c => c + c).join('')
          return {
            isValid: true,
            normalizedColor: `color: #${expanded}`
          }
        }
        return {
          isValid: true,
          normalizedColor: `color: ${colorValue}`
        }
      }

      // Check if it's a named color (basic validation)
      const namedColors = ['red', 'blue', 'green', 'yellow', 'orange', 'purple', 'gray', 'black', 'white']
      if (namedColors.includes(colorValue.toLowerCase())) {
        return {
          isValid: true,
          normalizedColor: `color: ${colorValue}`
        }
      }

      return {
        isValid: false,
        normalizedColor: 'color: #888',
        error: `Unrecognized color format: ${colorValue}`
      }
    } catch (error) {
      return {
        isValid: false,
        normalizedColor: 'color: #888',
        error: `Color validation exception: ${error}`
      }
    }
  }

  private levelPriority = {
    [LogLevel.DEBUG]: 0,
    [LogLevel.INFO]: 1,
    [LogLevel.WARN]: 2,
    [LogLevel.ERROR]: 3
  }

  constructor() {
    // Enable logging in development by default
    if (this.isProduction) {
      this.config.level = LogLevel.ERROR
      this.config.enabled = true
    } else if (import.meta.env.DEV) {
      this.enable()
    }
    
    // For local dev, prefer console-only logging; Sentry can be enabled via enableSentryVerbose()
  }

  enable() {
    this.config.enabled = true
    localStorage.setItem('debug', 'true')
  }

  disable() {
    this.config.enabled = false
    localStorage.removeItem('debug')
  }

  setLevel(level: LogLevel) {
    this.config.level = level
  }

  isEnabled(): boolean {
    return this.config.enabled
  }

  // Enable sending debug/info logs to Sentry
  enableSentryVerbose() {
    this.sentryVerbose = true
    localStorage.setItem('sentryVerbose', 'true')
    this.info('🔍 Sentry verbose logging enabled - debug/info logs will be sent to Sentry')
  }

  // Disable sending debug/info logs to Sentry
  disableSentryVerbose() {
    this.sentryVerbose = false
    localStorage.removeItem('sentryVerbose')
    this.info('🔕 Sentry verbose logging disabled - only errors/warnings will be sent to Sentry')
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isProduction) {
      return level === LogLevel.ERROR
    }

    if (!this.config.enabled) {
      return false
    }

    return this.levelPriority[level] >= this.levelPriority[this.config.level]
  }

  private formatMessage(level: LogLevel, message: string, requestId?: string): string {
    const parts = []
    
    if (this.config.includeTimestamp) {
      parts.push(`[${new Date().toISOString()}]`)
    }
    
    parts.push(`[${level}]`)
    
    if (requestId && this.config.includeRequestId) {
      parts.push(`[REQ-${requestId}]`)
    }
    
    parts.push(message)
    
    return parts.join(' ')
  }

  private log(level: LogLevel, message: string, data?: LogData, requestId?: string) {
    if (!this.shouldLog(level)) return

    const formattedMessage = this.formatMessage(level, message, requestId)
    const style = this.colors[level]

    // Add to Sentry breadcrumbs
    const breadcrumbLevel = level === LogLevel.ERROR ? 'error' :
                           level === LogLevel.WARN ? 'warning' :
                           level === LogLevel.INFO ? 'info' : 'debug'
    
    Sentry.addBreadcrumb({
      category: 'logger',
      message: message,
      level: breadcrumbLevel as Sentry.SeverityLevel,
      data: {
        ...( typeof data === 'object' && data !== null ? data : { value: data }),
        requestId,
      },
      timestamp: Date.now() / 1000,
    })

    // ENHANCED: Send important debug/info logs to Sentry as events (not just breadcrumbs)
    // This allows querying them with sentry-cli
    const shouldSendToSentry = this.shouldSendToSentry(level, message)
        if (this.sentryVerbose && shouldSendToSentry && (level === LogLevel.INFO || level === LogLevel.DEBUG)) {
      // Use captureMessage for non-error logs to make them queryable
      Sentry.withScope(scope => {
        scope.setLevel(breadcrumbLevel as Sentry.SeverityLevel)
        scope.setTag('log_level', level)
        scope.setTag('component', 'frontend')
        scope.setTag('log_type', 'debug_stream')
        if (requestId) scope.setTag('request_id', requestId)
            if (data && typeof data === 'object') {
              scope.setContext('log_data', data as Record<string, unknown>)
            }
        
        // Send as a message event
        Sentry.captureMessage(message, breadcrumbLevel as Sentry.SeverityLevel)
      })
    }

    if (data !== undefined) {
      console.log(`%c${formattedMessage}`, style, data)
    } else {
      console.log(`%c${formattedMessage}`, style)
    }
  }

  // Determine if a log should be sent to Sentry based on patterns
  // Classify error severity based on message and error details
  private classifyErrorSeverity(message: string, errorDetails?: unknown): ErrorSeverity {
    const fullErrorText = `${message} ${JSON.stringify(errorDetails || {})}`

    // Check if error matches any noise patterns
    if (ERROR_PATTERNS[ErrorSeverity.NOISE].some(pattern => pattern.test(fullErrorText))) {
      return ErrorSeverity.NOISE
    }

    // Check if error matches any expected patterns
    if (ERROR_PATTERNS[ErrorSeverity.EXPECTED].some(pattern => pattern.test(fullErrorText))) {
      return ErrorSeverity.EXPECTED
    }

    // Default to important for unclassified errors
    return ErrorSeverity.IMPORTANT
  }

  // Generate a fingerprint for error deduplication
  private generateErrorFingerprint(message: string, errorDetails?: unknown): string {
    // Create a fingerprint based on message and error type
    const errorType = errorDetails && typeof errorDetails === 'object' && 'name' in errorDetails
      ? String(errorDetails.name)
      : 'unknown'
    const messageHash = message.substring(0, 100) // First 100 chars
    return `${errorType}:${messageHash}`
  }

  // Check if an error should be sent to Sentry based on rate limiting and severity
  private shouldSendErrorToSentry(message: string, errorDetails?: unknown): boolean {
    // First, classify the error severity
    const severity = this.classifyErrorSeverity(message, errorDetails)

    // NOISE errors: Never send to Sentry (console only)
    if (severity === ErrorSeverity.NOISE) {
      return false
    }

    // EXPECTED errors: Only send breadcrumb, not full error event
    if (severity === ErrorSeverity.EXPECTED) {
      // Add as breadcrumb for debugging context
      Sentry.addBreadcrumb({
        category: 'expected_error',
        message: message,
        level: 'warning',
        data: {
          errorDetails: typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails),
        },
      })
      return false
    }

    // IMPORTANT and CRITICAL: Apply rate limiting
    const fingerprint = this.generateErrorFingerprint(message, errorDetails)
    const now = Date.now()
    const lastSent = this.errorFingerprints.get(fingerprint)

    // If we've never sent this error, or it's been more than the rate limit window, send it
    if (!lastSent || (now - lastSent) > this.ERROR_RATE_LIMIT_WINDOW) {
      this.errorFingerprints.set(fingerprint, now)

      // Clean up old fingerprints periodically (keep only last 100)
      if (this.errorFingerprints.size > 100) {
        const entries = Array.from(this.errorFingerprints.entries())
        // Sort by timestamp and keep only the 50 most recent
        entries.sort((a, b) => b[1] - a[1])
        this.errorFingerprints = new Map(entries.slice(0, 50))
      }

      return true
    }

    // This error was sent recently, skip it
    return false
  }

  private shouldSendToSentry(level: LogLevel, message: string): boolean {
    // Always send errors and warnings (they'll be rate limited separately)
    if (level === LogLevel.ERROR || level === LogLevel.WARN) return true

    // Patterns for important UI debugging logs
    const importantPatterns = [
      /progress/i,
      /status.*icon/i,
      /trial.*count/i,
      /api.*response/i,
      /real-time.*update/i,
      /missing.*field/i,
      /unexpected.*status/i,
      /pagination/i,
      /study.*debug/i,
      /ETH.*study/i  // Specific study debugging
    ]

    return importantPatterns.some(pattern => pattern.test(message))
  }

  debug(message: string, data?: LogData, requestId?: string) {
    this.log(LogLevel.DEBUG, message, data, requestId)
  }

  info(message: string, data?: LogData, requestId?: string) {
    this.log(LogLevel.INFO, message, data, requestId)
  }

  warn(message: string, data?: LogData | unknown, requestId?: string) {
    // If data looks like an error object, format it properly
    if (data && (isError(data) || (isRecord(data) && 'message' in data))) {
      const errorDetails = formatError(data)
      this.log(LogLevel.WARN, message, errorDetails as LogData, requestId)
    } else {
      this.log(LogLevel.WARN, message, data as LogData, requestId)
    }
  }

  error(message: string, error?: unknown, requestId?: string) {
    if (!this.shouldLog(LogLevel.ERROR)) return

    const formattedMessage = this.formatMessage(LogLevel.ERROR, message, requestId)
    const errorDetails = error !== undefined ? formatError(error) : undefined

    // Log to console with properly formatted error
    console.error(`%c${formattedMessage}`, this.colors[LogLevel.ERROR])

    if (errorDetails !== undefined) {
      console.error('Error details:', errorDetails)
    }

    // Rate limit error sending to Sentry to prevent duplicates
    if (!this.shouldSendErrorToSentry(message, errorDetails)) {
      // This error was sent recently, skip Sentry but still log locally
      return
    }

    // Send to Sentry with proper formatting and rate limiting applied
    if (errorDetails !== undefined) {
      // Capture to Sentry with properly formatted error details
      if (isError(error)) {
        Sentry.captureException(error, {
          contexts: {
            logger: {
              message,
              requestId,
              errorDetails: typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails),
            },
          },
          tags: {
            source: 'logger',
          },
        })
      } else {
        // For non-Error objects, capture as a message with properly formatted context
        const errorMessage = typeof errorDetails === 'string'
          ? errorDetails
          : typeof errorDetails === 'object' && 'message' in errorDetails
            ? String(errorDetails.message)
            : JSON.stringify(errorDetails)

        Sentry.captureMessage(`${message}: ${errorMessage}`, {
          level: 'error',
          contexts: {
            logger: {
              message,
              requestId,
              errorDetails: typeof errorDetails === 'string' ? errorDetails : errorDetails,
            },
          },
          tags: {
            source: 'logger',
            errorType: 'non-error-object',
          },
        })
      }
    } else {
      // No error object provided, just capture the message
      Sentry.captureMessage(message, {
        level: 'error',
        contexts: {
          logger: {
            message,
            requestId,
          },
        },
        tags: {
          source: 'logger',
        },
      })
    }
  }

  // Generate a unique request ID
  generateRequestId(): string {
    this.requestCounter++
    const id = `${Date.now()}-${this.requestCounter}`
    return id.slice(-loggerConfig.requestIdLength) // Keep configured length
  }

  // Log API request
  logRequest(requestId: string, method: string, url: string, options?: RequestInit) {
    if (!this.shouldLog(LogLevel.DEBUG)) return

    const startTime = Date.now()
    this.activeRequests.set(requestId, startTime)

    this.info(`→ ${method} ${url}`, {
      requestId,
      method,
      url,
      headers: options?.headers,
      body: options?.body ? this.parseBody(options.body) : undefined
    }, requestId)
  }

  // Log API response
  logResponse(requestId: string, status: number, statusText: string, data?: LogData, headers?: Headers) {
    if (!this.shouldLog(LogLevel.DEBUG)) return

    const startTime = this.activeRequests.get(requestId)
    const duration = startTime ? Date.now() - startTime : 0
    this.activeRequests.delete(requestId)

    const level = status >= 400 ? LogLevel.ERROR : LogLevel.INFO
    const arrow = status >= 400 ? '✗' : '✓'

    this.log(level, `← ${arrow} ${status} ${statusText} (${duration}ms)`, {
      requestId,
      status,
      statusText,
      duration: `${duration}ms`,
      headers: headers ? this.headersToObject(headers) : undefined,
      data: data
    }, requestId)
  }

  // Log retry attempt
  logRetry(requestId: string, attempt: number, maxRetries: number, delay: number, error: unknown) {
    this.warn(`🔄 Retry attempt ${attempt}/${maxRetries} after ${delay}ms`, {
      requestId,
      attempt,
      maxRetries,
      delay,
      error: formatError(error)
    }, requestId)
  }

  // Log timeout
  logTimeout(requestId: string, timeout: number) {
    this.error(`⏱️ Request timeout after ${timeout}ms`, undefined, requestId)
  }

  // Helper to parse request body
  private parseBody(body: BodyInit | null | undefined): LogData {
    if (body === null || body === undefined) {
      return undefined
    }
    
    if (typeof body === 'string') {
      try {
        return JSON.parse(body) as Record<string, unknown>
      } catch {
        return body
      }
    }
    
    // For FormData, URLSearchParams, etc., return a string representation
    if (body instanceof FormData) {
      const entries: Record<string, unknown> = {}
      body.forEach((value, key) => {
        entries[key] = value
      })
      return entries
    }
    
    if (body instanceof URLSearchParams) {
      return body.toString()
    }
    
    // For other types (Blob, ArrayBuffer, etc.), return a descriptive string
    if (body instanceof Blob) {
      return `[Blob: ${body.size} bytes, type: ${body.type}]`
    }
    
    if (body instanceof ArrayBuffer) {
      return `[ArrayBuffer: ${body.byteLength} bytes]`
    }
    
    if (body instanceof DataView) {
      return `[DataView: ${body.byteLength} bytes]`
    }
    
    // For ReadableStream, we can't easily serialize it
    if (body instanceof ReadableStream) {
      return '[ReadableStream]'
    }
    
    return '[Unknown body type]'
  }

  // Helper to convert Headers to object
  private headersToObject(headers: Headers): Record<string, string> {
    const obj: Record<string, string> = {}
    headers.forEach((value, key) => {
      obj[key] = value
    })
    return obj
  }

  // Create a group for related logs
  group(label: string) {
    if (this.config.enabled) {
      console.group(label)
    }
  }

  groupEnd() {
    if (this.config.enabled) {
      console.groupEnd()
    }
  }

  // Log a table for structured data
  table(data: readonly unknown[], columns?: string[]) {
    if (this.config.enabled) {
      console.table(data, columns)
    }
  }

  // Set user context in Sentry
  setUser(user: { id?: string; email?: string; username?: string } | null) {
    Sentry.setUser(user)
    this.info('User context updated', user ? { userId: user.id } : { cleared: true })
  }

  // Add custom context to Sentry
  setContext(key: string, context: Record<string, unknown>) {
    Sentry.setContext(key, context as Record<string, unknown>)
    this.debug(`Context '${key}' updated`, context)
  }

  // Add tags to Sentry
  setTag(key: string, value: string | number | boolean) {
    Sentry.setTag(key, value)
    this.debug(`Tag '${key}' set`, { [key]: value })
  }

  // Start a performance transaction
  startTransaction(name: string, op: string = 'navigation'): { finish: () => void; setData: (key: string, value: unknown) => void; setStatus: (status: string) => void } {
    // In Sentry v8, startTransaction is replaced with startActiveSpan
    const transaction = {
      finish: () => {},
      setData: () => {},
      setStatus: () => {}
    }
    
    // In Sentry v8, span operations are simplified
    // For now, we'll use a no-op implementation since the transaction API has changed
    transaction.finish = () => {}
    transaction.setData = () => {}
    transaction.setStatus = () => {}
    
    this.debug(`Transaction started: ${name}`, { op })
    
    return transaction
  }

  // Measure performance of a function
  async measurePerformance<T>(
    name: string,
    fn: () => T | Promise<T>,
    options?: { op?: string; data?: Record<string, unknown> }
  ): Promise<T> {
    const transaction = this.startTransaction(name, options?.op)
    
    if (options?.data) {
      Object.entries(options.data).forEach(([key, value]) => {
        transaction.setData(key, value)
      })
    }
    
    const startTime = performance.now()
    
    try {
      const result = await fn()
      
      const duration = performance.now() - startTime
      transaction.setStatus('ok')
      
      this.debug(`Performance: ${name}`, { 
        duration: `${duration.toFixed(2)}ms`,
        status: 'success',
        ...options?.data
      })
      
      return result
    } catch (error) {
      const duration = performance.now() - startTime
      transaction.setStatus('internal_error')
      
      this.error(`Performance: ${name} failed`, error)
      this.debug(`Performance: ${name}`, { 
        duration: `${duration.toFixed(2)}ms`,
        status: 'error',
        ...options?.data
      })
      
      throw error
    } finally {
      transaction.finish()
    }
  }

  // Add a custom breadcrumb
  addBreadcrumb(breadcrumb: {
    message: string
    category?: string
    level?: 'debug' | 'info' | 'warning' | 'error'
    data?: Record<string, unknown>
  }) {
    Sentry.addBreadcrumb({
      ...breadcrumb,
      timestamp: Date.now() / 1000,
    })
  }

  // Capture a message to Sentry
  captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
    Sentry.captureMessage(message, level)
    this.info(`Message captured to Sentry: ${message}`, { level })
  }

  // Capture an exception with context
  captureException(error: Error, context?: Record<string, unknown>) {
    if (context) {
      Sentry.withScope(scope => {
        // Handle special sentryTags property
        const sentryTags = (context as Record<string, unknown>)['sentryTags'] as Record<string, unknown> | undefined
        if (sentryTags && typeof sentryTags === 'object') {
          Object.entries(sentryTags).forEach(([key, value]) => {
            scope.setTag(key, String(value))
          })
          // Remove sentryTags from context to avoid duplication
          const restContext = { ...context }
          delete restContext['sentryTags']
          context = restContext
        }
        
        // Set remaining properties as context
        Object.entries(context || {}).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            scope.setContext(key, value as Record<string, unknown>)
          } else {
            scope.setExtra(key, value)
          }
        })
        
        Sentry.captureException(error)
      })
    } else {
      Sentry.captureException(error)
    }
    
    this.error('Exception captured to Sentry', error)
  }
}

// Export singleton instance
export const logger = new Logger()

// Export for testing or creating custom instances
export { Logger }
